import { useState, useEffect, useRef } from "react";
import {
  MobileSheet,
  MobileSheetContent,
  MobileSheetHeader,
  MobileSheetTitle,
  MobileSheetDescription,
} from "@/components/ui/mobile-sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronUp, CheckSquare, Baby } from "lucide-react";
import { useFoodLogs } from "@/hooks/useFoodLogs";
import { useBabySelection } from "@/hooks/useBabySelection";
import { useAuth } from "@/contexts/AuthContext";
import { PREFERENCE_OPTIONS } from "@/constants/preferences";
import type { PreferenceType } from "@/types/ui";
import { toast } from "sonner";
import { foodLogSchema, timeStringSchema } from "@/utils/validation";
import { ImageCapture } from "@/components/ImageCapture";
import { supabase } from "@/integrations/supabase/client";
import { useStreaks } from "@/hooks/useStreaks";
import { useAchievements } from "@/hooks/useAchievements";
import { compressImage } from "@/utils/imageCompression";
import { haptic } from "@/utils/haptics";

interface UnifiedFoodLogDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  foodId: string;
  foodName: string;
}

export function UnifiedFoodLogDialog({ 
  open, 
  onOpenChange, 
  foodId, 
  foodName
}: UnifiedFoodLogDialogProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customTime, setCustomTime] = useState('');
  const [preference, setPreference] = useState<PreferenceType | ''>('');
  const [allergyReaction, setAllergyReaction] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedBabyId, setSelectedBabyId] = useState('');
  const userPickedRef = useRef(false); // locks selection once user manually picks
  const [capturedImage, setCapturedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const { addFoodLog } = useFoodLogs();
  const { babies, babiesLoading, handleSelectBaby } = useBabySelection();
  const { selectedBaby: globalSelectedBaby, user } = useAuth();
  const { updateStreak } = useStreaks(selectedBabyId);
  const { checkAndUnlockAchievement } = useAchievements(selectedBabyId);

  // Initialize baby selection ONLY when dialog opens.
  // Once opened, dialog selection is fully decoupled from global state changes
  // so background refreshes and shared-access updates can never revert the user's pick.
  useEffect(() => {
    if (!open) return;
    if (userPickedRef.current) return;
    if (selectedBabyId) return;

    const initialId = globalSelectedBaby?.id ?? babies[0]?.id ?? '';
    if (initialId) setSelectedBabyId(initialId);
  }, [open, selectedBabyId, globalSelectedBaby, babies]);

  // Reset on close so next open re-initializes from global state
  useEffect(() => {
    if (!open) {
      setSelectedBabyId('');
      userPickedRef.current = false;
    }
  }, [open]);

  const handleBabyChange = (babyId: string) => {
    if (!babyId) return;
    userPickedRef.current = true;
    setSelectedBabyId(babyId);
    const selectedBaby = babies.find(baby => baby.id === babyId);
    if (selectedBaby) {
      // Update global selection so the rest of the app remembers this choice,
      // but our local userPickedRef guarantees the dialog won't revert.
      handleSelectBaby(selectedBaby);
    }
  };

  // Set current time when dialog opens
  useEffect(() => {
    if (open) {
      const now = new Date();
      const timeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      setCustomTime(timeString);
    }
  }, [open]);

  const handleSubmit = async () => {
    // Validate baby selection
    if (!selectedBabyId) {
      toast.error('Vennligst velg et barn');
      return;
    }

    // Validate time format
    if (!customTime || !customTime.match(/^\d{2}:\d{2}$/)) {
      toast.error('Vennligst velg et tidspunkt');
      return;
    }

    try {
      // Parse time (HH:MM format)
      const today = new Date();
      const [hours, minutes] = customTime.split(':').map(Number);
      const consumedAt = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);

      let photoUrl = null;
      let photoStoragePath = null;

      // Upload image if captured
      if (capturedImage && user) {
        setUploadingImage(true);
        try {
          const compressed = await compressImage(capturedImage, { maxDimension: 1400, quality: 0.82 });
          const fileExt = compressed.name.split('.').pop();
          const fileName = `${user.id}/${Date.now()}.${fileExt}`;

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('food-images')
            .upload(fileName, compressed, {
              cacheControl: '3600',
              upsert: false,
              contentType: compressed.type,
            });

          if (uploadError) throw uploadError;

          // Get public URL
          const { data: { publicUrl } } = supabase.storage
            .from('food-images')
            .getPublicUrl(fileName);

          photoUrl = publicUrl;
          photoStoragePath = fileName;
        } catch (error) {
          console.error('Image upload failed:', error);
          toast.error('Kunne ikke laste opp bilde, men matloggen lagres');
        } finally {
          setUploadingImage(false);
        }
      }

      // Validate food log data
      const logData = {
        consumed_at: consumedAt.toISOString(),
        preference: preference === '' ? undefined : preference,
        allergy_reaction: allergyReaction.trim() === '' ? undefined : allergyReaction.trim(),
        notes: notes.trim() === '' ? undefined : notes.trim(),
      };
      
      const validationResult = foodLogSchema.safeParse(logData);
      
      if (!validationResult.success) {
        const firstError = validationResult.error.errors[0];
        toast.error(firstError.message);
        return;
      }

      const logResult = await addFoodLog({
        food_id: foodId,
        baby_id: selectedBabyId,
        photo_url: photoUrl,
        photo_storage_path: photoStoragePath,
        ...logData,
      });

      if (logResult) {
        // Update streak and check for achievements
        const streakResult = await updateStreak();
        
        if (streakResult) {
          if (streakResult.milestoneMet) {
            // Show celebration toast
            toast.success(`🔥 ${streakResult.streak}-dagers streak!`, {
              description: "Fantastisk! Fortsett sånn!",
            });
          }

          // Check streak achievements
          if (streakResult.streak === 7) {
            await checkAndUnlockAchievement('streak_7', 7);
          } else if (streakResult.streak === 30) {
            await checkAndUnlockAchievement('streak_30', 30);
          } else if (streakResult.streak === 100) {
            await checkAndUnlockAchievement('streak_100', 100);
          }
        }

        // Check photo achievements
        if (photoUrl) {
          // Count total photos from logs
          const { data: logsWithPhotos } = await supabase
            .from('food_logs')
            .select('id')
            .eq('user_id', user?.id)
            .eq('baby_id', selectedBabyId)
            .not('photo_url', 'is', null);

          const photosCount = (logsWithPhotos?.length || 0);
          if (photosCount >= 25) {
            await checkAndUnlockAchievement('photographer', photosCount);
          }
          if (photosCount >= 100) {
            await checkAndUnlockAchievement('memory_keeper', photosCount);
          }
        }

        // Check food exploration achievements  
        const { data: allLogs } = await supabase
          .from('food_logs')
          .select('food_id')
          .eq('user_id', user?.id)
          .eq('baby_id', selectedBabyId);

        const uniqueFoodsCount = new Set(allLogs?.map(log => log.food_id)).size;
        if (uniqueFoodsCount === 1) {
          await checkAndUnlockAchievement('first_food', 1);
        } else if (uniqueFoodsCount >= 10) {
          await checkAndUnlockAchievement('foods_10', uniqueFoodsCount);
        }
        if (uniqueFoodsCount >= 25) {
          await checkAndUnlockAchievement('foods_25', uniqueFoodsCount);
        }
        if (uniqueFoodsCount >= 50) {
          await checkAndUnlockAchievement('foods_50', uniqueFoodsCount);
        }
      }

      // Reset and close
      setCustomTime('');
      setPreference('');
      setAllergyReaction('');
      setNotes('');
      setCapturedImage(null);
      setImagePreview('');
      setShowAdvanced(false);
      onOpenChange(false);
      
      haptic('success');
      const thumb = imagePreview || undefined;
      toast.success(`${foodName} ble loggført!`, {
        description: 'Bra jobba! Loggen er lagret.',
        icon: thumb ? (
          <img
            src={thumb}
            alt=""
            className="h-10 w-10 rounded-md object-cover"
          />
        ) : '🍽️',
      });
    } catch (error) {
      console.error('Error logging food:', error);
      toast.error('Kunne ikke logge mat');
    }
  };

  return (
    <MobileSheet open={open} onOpenChange={onOpenChange}>
      <MobileSheetContent className="sm:max-w-md">
        <MobileSheetHeader>
          <MobileSheetTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-primary" />
            Logg {foodName}
          </MobileSheetTitle>
          <MobileSheetDescription>
            Rask loggføring - legg til detaljer om nødvendig
          </MobileSheetDescription>
        </MobileSheetHeader>

        <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
          {/* Baby Selection - ALWAYS SHOW */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Baby className="h-4 w-4" />
              Barn
            </Label>
            <Select 
              value={selectedBabyId} 
              onValueChange={handleBabyChange}
              disabled={babiesLoading || babies.length === 0}
            >
              <SelectTrigger onClick={(e) => e.stopPropagation()}>
                <SelectValue placeholder={
                  babiesLoading ? "Laster barn..." : 
                  babies.length === 0 ? "Ingen barn funnet" : 
                  "Velg barn"
                } />
              </SelectTrigger>
              <SelectContent>
                {babies.map(baby => (
                  <SelectItem key={baby.id} value={baby.id}>
                    <span className="flex items-center gap-2">
                      <Baby className="h-4 w-4" />
                      {baby.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {babies.length === 0 && !babiesLoading && (
              <p className="text-xs text-muted-foreground mt-1">
                💡 Legg til et barn i profilen din først
              </p>
            )}
          </div>

          {/* Time Selection */}
          <div className="space-y-2">
            <Label htmlFor="time-input">Tidspunkt</Label>
            <Input
              id="time-input"
              type="time"
              value={customTime}
              onChange={(e) => {
                e.stopPropagation();
                setCustomTime(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              className="w-full"
              required
            />
          </div>

          {/* Image Capture */}
          <div className="space-y-2">
            <Label>📸 Legg til bilde (valgfritt)</Label>
            <ImageCapture
              onImageCapture={(file, preview) => {
                setCapturedImage(file);
                setImagePreview(preview);
              }}
              onRemove={() => {
                setCapturedImage(null);
                setImagePreview('');
              }}
              value={imagePreview}
            />
            <p className="text-xs text-muted-foreground">
              💡 Tips: Ta bilde av måltidet for å huske hva barnet spiste
            </p>
          </div>

          {/* Preference */}
          <div className="space-y-2">
            <Label>Hvordan likte barnet maten?</Label>
            <Select value={preference} onValueChange={(value) => setPreference(value as PreferenceType | '')}>
              <SelectTrigger onClick={(e) => e.stopPropagation()}>
                <SelectValue placeholder="Velg (valgfritt)" />
              </SelectTrigger>
              <SelectContent>
                {PREFERENCE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Advanced Details Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setShowAdvanced(!showAdvanced);
            }}
            className="w-full"
          >
            {showAdvanced ? (
              <>
                <ChevronUp className="h-4 w-4 mr-2" />
                Skjul detaljer
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-2" />
                Legg til detaljer
              </>
            )}
          </Button>

          {/* Advanced Fields */}
          {showAdvanced && (
            <div className="space-y-4 pt-2 border-t">
              <div className="space-y-2">
                <Label>Allergisk reaksjon (valgfritt)</Label>
                <Input
                  placeholder="F.eks. utslett, magesmerter, ingen reaksjon"
                  value={allergyReaction}
                  onChange={(e) => {
                    e.stopPropagation();
                    setAllergyReaction(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <div className="space-y-2">
                <Label>Notater (valgfritt)</Label>
                <Textarea
                  placeholder="Ekstra notater om matopplevelsen..."
                  value={notes}
                  onChange={(e) => {
                    e.stopPropagation();
                    setNotes(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              variant="outline" 
              className="flex-1" 
              onClick={(e) => {
                e.stopPropagation();
                onOpenChange(false);
              }}
            >
              Avbryt
            </Button>
            <Button 
              className="flex-1 bg-primary hover:bg-primary/90" 
              onClick={(e) => {
                e.stopPropagation();
                handleSubmit();
              }}
              disabled={babiesLoading || babies.length === 0 || uploadingImage}
            >
              {uploadingImage ? 'Laster opp bilde...' : 'Lagre'}
            </Button>
          </div>
        </div>
      </MobileSheetContent>
    </MobileSheet>
  );
}
